import type { ChangeEvent } from "react"
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react"

import type { FileRejection } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"

import { Box, Divider, Grid } from "@mui/material"
import { InputText, RadioButton, RadioButtonGroup, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"
import { useFormikContext } from "formik"
import type { MRT_ColumnDef } from "material-react-table"

import { LoadingContainer } from "../../../components/core/LoadingContainer"
import { Dropzone } from "../../../components/form/Dropzone"
import { BaseTable } from "../../../components/tables/BaseTable"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { RequiredSchema } from "../../../types/custom/projects"
import type { TrainingDataResponse } from "../../../types/generated/api/TrainingDataResponse"
import type { TrainingDataUpload } from "../../../types/generated/api/TrainingDataUpload"
import type { UploadTrainingDataFlowProps, UploadTrainingDataFormikValues } from "../interfaces"

import styles from "../Projects.module.scss"

/**
 * Project creation overview, first screen in the creation flow
 * @param  {string} currentProjectId
 * @param  {function} onDataChange
 * @param  {string} type
 * @return  {React.ReactElement}
 */
export function UploadTrainingDataFlow(props: Readonly<UploadTrainingDataFlowProps>): React.ReactElement {
  const { currentProjectId, onDataChange, type, creditScoringType } = props

  const { t } = useTranslation("projects")

  const { values, errors, touched, handleChange, handleBlur } = useFormikContext<UploadTrainingDataFormikValues>()

  const [selectedDataset, setSelectedDataset] = useState("sample")

  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [invalidFiles, setInvalidFiles] = useState<FileRejection[]>([])

  const abortControllerRef = useRef<AbortController | null>(null)

  const [_, setOpenDropzone] = React.useState<null | (() => void)>(null)

  // Define table columns with translations
  const columns: MRT_ColumnDef<RequiredSchema>[] = [
    {
      header: t("uploadTrainingData.table.headers.name"),
      accessorKey: "name",
    },
    {
      header: t("uploadTrainingData.table.headers.required"),
      accessorKey: "required",
      enableColumnFilter: false,
    },
    {
      header: t("uploadTrainingData.table.headers.type"),
      accessorKey: "type",
    },
    {
      header: t("uploadTrainingData.table.headers.description"),
      accessorKey: "description",
    },
  ]

  const handleOpenDropzone = (open: () => void): void => {
    setOpenDropzone(() => open)
  }

  // https://github.com/TanStack/query/discussions/1551
  // Followed the instructions here by F0rro on jan 23 and cleaned up a bit to match our architecture
  const {
    isLoading: uploadingCSV,
    isError,
    mutateAsync: uploadCSVAsyncMutation,
    reset,
  } = useMutation<TrainingDataResponse, AxiosError, File>((file: File) => {
    abortControllerRef.current = new AbortController()
    return KonanAPI.uploadTrainingData(currentProjectId, file, setUploadProgress, abortControllerRef.current.signal)
  })

  //fetch all training data in current project
  const { data: trainingData, isLoading: isAllTrainingDataLoading } = useQuery<TrainingDataUpload[], AxiosError>(
    ["training-data", currentProjectId],
    () => KonanAPI.fetchTrainingData(currentProjectId),
    { enabled: currentProjectId != null },
  )

  //fetch schema
  const { isLoading: isSchemaLoading, data: schema } = useQuery<RequiredSchema[], AxiosError>(
    ["required-schema", currentProjectId],
    () => KonanAPI.fetchRequiredSchema(currentProjectId),
    { keepPreviousData: true },
  )

  // Select change handler
  const handleSelect = (trainingData: ChangeEvent<HTMLInputElement>): void => {
    setSelectedDataset((trainingData?.target).value)
    onDataChange((trainingData?.target).value)
  }

  // handle close dropzone
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    reset()
    setUploadProgress(0)
    setAcceptedFiles([])
    if (type === "pre-built") {
      setSelectedDataset("sample")
    }
  }, [reset, type])

  // handle upload once files are accepted
  const handleUpload = async (): Promise<void> => {
    setSelectedDataset("")
    onDataChange("")
    try {
      if (!acceptedFiles[0]) return

      const res = await uploadCSVAsyncMutation(acceptedFiles[0])
      onDataChange(res.uuid)

      // Invalidate training data to trigger a refresh
      await queryClient.invalidateQueries(["training-data", currentProjectId])
      setSelectedDataset(res.uuid)
    } catch {
      // reset dropzone on error
      handleCloseAndAbort()
    }
  }

  // starts uploading once the file is read
  useEffect(() => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleUpload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles])

  return (
    <Grid
      container
      item
      spacing={2}
      padding={0.5}
      className={creditScoringType === "pre-built" ? styles.prebuiltContainer : styles.automlContainer}
    >
      {/**
       * upload errors
       * 1. invalid file type
       * 2. request didn't succeed
       */}
      {invalidFiles && invalidFiles.length > 0 && (
        <Grid item xs={12}>
          <Snackbar
            severity="error"
            fullWidth
            description={t("uploadTrainingData.errors.invalidFile", { fileName: invalidFiles[0]?.file.name })}
          />
        </Grid>
      )}

      {isError && (
        <Grid item xs={12}>
          <Snackbar severity="error" fullWidth description={t("uploadTrainingData.errors.uploadError")} />
        </Grid>
      )}

      <Grid item xs={12}>
        <Snackbar severity="info" fullWidth description={t("uploadTrainingData.infoMessage")} />
      </Grid>

      <Grid item>
        <Typography variant="h3-bold">{t("uploadTrainingData.modelOverview")}</Typography>
      </Grid>

      <Grid item xs={12}>
        <InputText
          label={t("uploadTrainingData.fields.name")}
          id="name"
          placeholder={t("uploadTrainingData.fields.namePlaceholder")}
          required
          value={values && values.name}
          handleChange={(e) => {
            values && handleChange(e)
          }}
          hideDescription
          handleBlur={values && handleBlur}
          error={touched && touched.name && Boolean(errors && errors.name) && errors.name}
          fullWidth
        />
      </Grid>

      <div className={styles.verticalMargin} />

      {/* Schema table section */}
      {creditScoringType !== "auto-ml" && (
        <Fragment>
          <Grid item xs={12}>
            <Typography variant="h3-bold" gutterBottom>
              {t("uploadTrainingData.schema.title")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Grid item xs={12}>
              <BaseTable
                enableOrdering={false}
                columns={columns as MRT_ColumnDef[]}
                data={schema ?? []}
                isLoading={isSchemaLoading}
                title={t("uploadTrainingData.table.title")}
              />

              <Typography variant="label-regular" style={{ marginTop: "8px" }}>
                {t("uploadTrainingData.schema.description")}
              </Typography>
            </Grid>
          </Grid>
        </Fragment>
      )}

      {/* data upload section */}
      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        xs={12}
        rowSpacing={2}
      >
        <Grid item container direction="column" xs={12} lg={5.8}>
          <Grid item>
            <Typography variant="h3-bold" gutterBottom>
              {t("uploadTrainingData.upload.title")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Dropzone
              uploadingCSV={uploadingCSV}
              uploadProgress={uploadProgress}
              resetProgress={() => setUploadProgress(0)}
              selectedFiles={acceptedFiles}
              setSelectedFiles={(file: File[]) => setAcceptedFiles(file)}
              setInvalidFiles={(file: FileRejection[]) => setInvalidFiles(file)}
              bottomText={t("uploadTrainingData.upload.bottomText")}
              handleCancelUpload={handleCloseAndAbort}
              onDropZoneOpen={handleOpenDropzone}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} lg={5.8}>
          <Grid item>
            <Typography variant="h3-bold" gutterBottom>
              {creditScoringType === "pre-built"
                ? t("uploadTrainingData.existingData.titlePreBuilt")
                : t("uploadTrainingData.existingData.titleAutoML")}
            </Typography>
          </Grid>
          <Grid container item xs={12} direction="column" className={styles.existingDataContainer}>
            {creditScoringType === "pre-built" && (
              <Grid item xs={12}>
                <Grid
                  item
                  className={selectedDataset === "sample" ? styles.selectedTrainingDataCard : styles.trainingDataCard}
                  //onClick={() => handleSelect("sample")}
                >
                  <Typography variant="a" style={{ width: "100%" }} noWrap>
                    {t("uploadTrainingData.sampleDataset")}
                  </Typography>
                </Grid>
              </Grid>
            )}

            {isAllTrainingDataLoading && type === "dialog" ? (
              <Grid item container xs={12} direction="column" alignItems="center" justifyContent="center">
                <LoadingContainer />
              </Grid>
            ) : (trainingData && trainingData?.length === 0) || (!trainingData && isAllTrainingDataLoading) ? (
              <Grid item container xs={12} direction="column" alignItems="center" justifyContent="center">
                <Typography variant="a">{t("uploadTrainingData.noData.title")}</Typography>
                <Box mt={0.5} />
                <Typography variant="span-regular">{t("uploadTrainingData.noData.subtitle")}</Typography>
              </Grid>
            ) : (
              <RadioButtonGroup
                aria-label="datasets"
                name="controlled-radio-buttons-group"
                value={selectedDataset}
                onChange={handleSelect}
                alignment="vertical"
                hideDescription
                style={{ display: "inline-grid", width: "100%" }}
              >
                {trainingData?.map((dataset) => (
                  <Fragment key={dataset.uuid}>
                    <Grid
                      item
                      padding={2}
                      sx={{
                        input: {
                          minWidth: "1rem",
                          minHeight: "1rem",
                        },
                      }}
                    >
                      <RadioButton
                        value={dataset.uuid}
                        checked={dataset.uuid === selectedDataset}
                        label={dataset.name}
                      />
                    </Grid>

                    <Divider
                      variant="fullWidth"
                      style={{
                        backgroundColor: "var(--base-border-1)",
                      }}
                      sx={{
                        "&::before, &::after": {
                          backgroundColor: "var(--base-border-1)",
                        },
                      }}
                    />
                  </Fragment>
                ))}
              </RadioButtonGroup>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
